/**
 * @file MeetingRenameDialog.tsx
 * @brief Rename a meeting from its page (B48, DES 02-11-2026).
 *
 * @details
 * Opened by clicking the meeting title or from ••• > Rename, owner only.
 * PATCH /api/boardroom/sessions/:id { title } (trimmed, 1-200 characters);
 * the linked scheduled meeting follows on the server, and Zapier / Make / MCP
 * read the new title. The phone's twin is header ••• > Rename. Same look as
 * the Notes list's NoteRenameDialog.
 *
 * @author DES
 * @date 02-11-2026
 */

import { useEffect, useState } from "react";
import { Presentation } from "lucide-react";
import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { ApiClientError } from "@/shared/api/client";
import { toast } from "@/shared/hooks/useToast";
import { useRenameMeeting } from "../api";

const MAX_TITLE = 200;

export function MeetingRenameDialog({
  open,
  onOpenChange,
  meetingId,
  title,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  meetingId: string;
  title: string;
}) {
  const rename = useRenameMeeting();
  const [value, setValue] = useState(title);
  useEffect(() => {
    if (open) setValue(title);
  }, [open, title]);
  // The server collapses spaces the same way.
  const next = value.replace(/\s+/g, " ").trim();

  const save = () => {
    if (!next || rename.isPending) return;
    if (next === title.trim()) {
      onOpenChange(false);
      return;
    }
    rename.mutate(
      { meetingId, title: next },
      {
        onSuccess: () => {
          onOpenChange(false);
          toast({ variant: "success", title: "Meeting renamed" });
        },
        onError: (err) =>
          toast({
            variant: "destructive",
            title: "Could not rename",
            description: err instanceof ApiClientError ? err.message : "Please try again.",
          }),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Rename meeting</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            save();
          }}
        >
          <label htmlFor="meeting-title" className="text-sm font-semibold">
            Meeting title
          </label>
          <div className="relative mt-2">
            <Presentation className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              capitalizeFirst
              id="meeting-title"
              autoFocus
              // The whole title selected, ready to retype.
              onFocus={(e) => e.currentTarget.select()}
              value={value}
              maxLength={MAX_TITLE}
              placeholder="Enter a title"
              onChange={(e) => setValue(e.target.value)}
              className="h-12 pl-10"
            />
          </div>
          <DialogFooter className="mt-5 gap-2">
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!next || rename.isPending}>
              {rename.isPending ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
